import { HugeiconsIcon } from "@hugeicons/react"
import {
  Image01Icon,
  Video01Icon,
  TextAlignLeftIcon,
  ViewIcon,
  ViewOffIcon,
  SquareLock02Icon,
  SquareUnlock02Icon,
  Layout01Icon,
  ArrowUp01Icon,
  ArrowDown01Icon,
} from "@hugeicons/core-free-icons"
import { useCompositorStore } from "@/lib/stores/compositor-store"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import type { CompositorLayer } from "@/lib/types/compositor"
import { cn } from "@/lib/utils"

const TEXT_MODULES = ["text_input", "llm_text", "text_builder"]

const LAYER_TYPE_ICONS = {
  background: Layout01Icon,
  image: Image01Icon,
  video: Video01Icon,
  text: TextAlignLeftIcon,
}

export function CompositorLayersSidebar() {
  const config = useCompositorStore((s) => s.config)
  const nodeId = useCompositorStore((s) => s.nodeId)
  const selectedItem = useCompositorStore((s) => s.selectedItem)
  const selectItem = useCompositorStore((s) => s.selectItem)
  const updateLayer = useCompositorStore((s) => s.updateLayer)
  const reorderLayers = useCompositorStore((s) => s.reorderLayers)

  function moveLayer(layer: CompositorLayer, direction: "up" | "down") {
    if (!config) return
    const layers = config.layers
    const idx = layers.indexOf(layer)
    // "up" in display (higher z) = higher index in array; "down" = lower index
    // protect background: it stays at index 0
    const swapIdx = direction === "up" ? idx + 1 : idx - 1
    if (swapIdx < 0 || swapIdx >= layers.length) return
    if (layers[swapIdx].type === "background" && direction === "down") return
    const next = [...layers]
    ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
    reorderLayers(next)
  }
  const pipelineEdges = usePipelineStore((s) => s.edges)
  const pipelineNodes = usePipelineStore((s) => s.nodes)

  function getEffectiveIcon(layer: CompositorLayer) {
    if (layer.type !== "image") return LAYER_TYPE_ICONS[layer.type]
    if (!nodeId) return LAYER_TYPE_ICONS.image
    const edge = pipelineEdges.find((e) => e.target === nodeId && e.targetHandle === layer.id)
    if (!edge) return LAYER_TYPE_ICONS.image
    const src = pipelineNodes.find((n) => n.id === edge.source)
    return src && TEXT_MODULES.includes(src.data.module) ? LAYER_TYPE_ICONS.text : LAYER_TYPE_ICONS.image
  }

  if (!config) return null

  // Display layers in reverse z-order (topmost first)
  const displayLayers = [...config.layers].reverse()

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border/50">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Layers</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Canvas root item */}
        <button
          onClick={() => selectItem({ type: "canvas" })}
          className={cn(
            "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent/50 transition-colors",
            selectedItem?.type === "canvas" && "bg-accent"
          )}
        >
          <span className="text-[11px] font-medium text-foreground">Canvas</span>
          <span className="ml-auto text-[10px] text-muted-foreground">
            {config.canvas.width}×{config.canvas.height}
          </span>
        </button>

        {/* Layer list */}
        {displayLayers.map((layer: CompositorLayer) => {
          const Icon = getEffectiveIcon(layer)
          const isSelected = selectedItem?.type === "layer" && selectedItem.layerId === layer.id

          return (
            <button
              key={layer.id}
              onClick={() => selectItem({ type: "layer", layerId: layer.id })}
              className={cn(
                "group w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-accent/50 transition-colors",
                isSelected && "bg-accent",
                layer.type === "background" && "pl-3"
              )}
            >
              <HugeiconsIcon
                icon={Icon}
                size={13}
                className={cn("shrink-0 text-muted-foreground", isSelected && "text-foreground")}
              />
              <span className={cn(
                "flex-1 text-[11px] truncate text-muted-foreground",
                isSelected && "text-foreground font-medium"
              )}>
                {layer.name}
              </span>
              {/* Hover-reveal controls */}
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {layer.type !== "background" && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveLayer(layer, "up") }}
                      className="p-0.5 hover:text-foreground text-muted-foreground/60"
                      title="Move up"
                    >
                      <HugeiconsIcon icon={ArrowUp01Icon} size={11} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveLayer(layer, "down") }}
                      className="p-0.5 hover:text-foreground text-muted-foreground/60"
                      title="Move down"
                    >
                      <HugeiconsIcon icon={ArrowDown01Icon} size={11} />
                    </button>
                  </>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); updateLayer(layer.id, { visible: !layer.visible }) }}
                  className="p-0.5 hover:text-foreground text-muted-foreground/60"
                >
                  <HugeiconsIcon icon={layer.visible ? ViewIcon : ViewOffIcon} size={11} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); updateLayer(layer.id, { locked: !layer.locked }) }}
                  className="p-0.5 hover:text-foreground text-muted-foreground/60"
                >
                  <HugeiconsIcon icon={layer.locked ? SquareLock02Icon : SquareUnlock02Icon} size={11} />
                </button>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
